import type { ChannelFilters, ChannelOptions, ChannelSort } from 'stream-chat';

/**
 * Exports few channel list configuration options. See the docs for more information:
 * - https://getstream.io/chat/docs/sdk/react/core-components/channel_list/
 *
 * @param user the user id.
 */
export const getChannelListOptions = (user: string, eventId: number) => {

  /**
   * FILTERS: ChannelFilters
   * Defines which channels to query from Stream API
   * CONDITIONS:
   * - type: 'messaging' → Only DM channels (not group channels)
   * - members: { $in: [user] } → User must be a member
   * - eventId: eventId → Channel must belong to current event
   * RESULT:
   * Queries all DM channels where current user is participant for this event
   */
  const filters: ChannelFilters = { type: 'messaging', members: { $in: [user!] }, eventId: eventId}
  
  /**
   * OPTIONS: ChannelOptions
   * Controls what data to load and how to track channel state
   * FLAGS:
   * - state: true → Load full channel state (messages, members, etc.)
   * - watch: true → Subscribe to real-time updates for these channels
   * - presence: true → Track user online/offline status
   * PERFORMANCE:
   * - state: true loads more data but provides complete channel info
   * - watch: true enables real-time without manual polling
   * - presence: true shows green/gray dots for online/offline users
   */
  const options: ChannelOptions = { state: true, watch: true, presence: true };

  /**
   * SORT: ChannelSort
   * Defines order in which channels appear in list
   * CRITERIA:
   * - last_message_at: -1 → Sort by last message time (newest first)
   * - updated_at: -1 → Secondary sort by channel update time (newest first)
   * USER EXPERIENCE:
   * Most active conversations stay at top of list
   * Users see recent chats without scrolling
   */
  const sort: ChannelSort = {
    last_message_at: -1,
    updated_at: -1,
  };

  /**
   * RETURN VALUE
   * Combined configuration object for ChannelList component
   * USAGE IN APP:
   * const channelListOptions = getChannelListOptions(userId, eventId);
   * <ChannelList filters={channelListOptions.filters} ... />
   */
  return {
    filters,
    options,
    sort,
  };
};
